import { Controller } from "@hotwired/stimulus"
import { lockFormDuringUpload } from "./active_storage/form_upload_lock"
import { renderStreamMessage } from "@hotwired/turbo"
import {
  applyPdfPageCount,
  detectCategoryFromFilename,
  directUpload,
  formatFileSize,
  isValidFileType,
  updateUploadedFile,
} from "./active_storage/upload_helpers"

function nextAnimationFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve))
}

async function waitForElement(getElement, { attempts = 15 } = {}) {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const element = getElement()
    if (element) return element
    await nextAnimationFrame()
  }

  return null
}

// Batch upload into nested form rows via Turbo Stream nested #new.
export default class extends Controller {
  static targets = [
    "list",
    "input",
    "trigger",
    "progressContainer",
    "progressBar",
    "controls",
    "statusText",
    "cancelButton",
  ]
  static values = {
    url: String,
    newFileUrl: String,
    acceptedTypes: String,
    maxFileSize: Number,
  }

  connect() {
    this.pendingRequest = new AbortController()
    this.cancelled = false
    this.uploading = false
  }

  disconnect() {
    this.cancelled = true
    this.pendingRequest.abort()
    this.removePendingField()
    this.resetUploadUi()
    if (this.hasInputTarget) this.inputTarget.disabled = false
    this.restoreSubmitButtons()
    clearTimeout(this.resetTimer)
  }

  handleFileSelect(event) {
    this.startUpload(Array.from(event.target.files))
  }

  dragOver(event) {
    event.preventDefault()
  }

  dropFiles(event) {
    event.preventDefault()
    this.startUpload(Array.from(event.dataTransfer.files))
  }

  startUpload(files) {
    if (this.uploading || files.length === 0) return
    this.cancelled = false
    if (this.hasInputTarget) this.inputTarget.value = ""
    clearTimeout(this.resetTimer)
    void this.uploadFiles(files)
  }

  cancelUpload() {
    this.cancelled = true
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = "Finishing the current file; remaining files cancelled."
  }

  async uploadFiles(files) {
    const signal = this.pendingRequest.signal
    this.showUploadUi()
    let failed = false

    for (const file of files) {
      if (this.cancelled) break

      let newField
      const delegate = {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            if (signal.aborted) return
            const progress = Math.round((event.loaded / event.total) * 100)
            if (this.hasProgressBarTarget) {
              this.progressBarTarget.style.width = `${progress}%`
            }
            if (this.hasStatusTextTarget) {
              this.statusTextTarget.textContent = `Uploading ${file.name} (${progress}%)`
            }
          })
        },
      }

      try {
        if (this.hasStatusTextTarget) {
          this.statusTextTarget.textContent = `Preparing to upload ${file.name}...`
        }
        if (this.hasProgressBarTarget) {
          this.progressBarTarget.style.width = "0%"
        }

        if (this.acceptedTypesValue && !isValidFileType(file, this.acceptedTypesValue)) {
          throw new Error("Please choose a supported file type.")
        }
        if (this.maxFileSizeValue > 0 && file.size > this.maxFileSizeValue) {
          throw new Error(`Each file must be ${formatFileSize(this.maxFileSizeValue)} or smaller.`)
        }
        newField = await this.appendNestedLineViaTurbo(signal)
        if (signal.aborted) return
        this.pendingField = newField
        const blob = await directUpload(file, this.urlValue, delegate)
        if (signal.aborted) return

        this.applyCategoryFromFilename(newField, file.name)

        updateUploadedFile(newField, blob)
        this.pendingField = null

        if (newField.querySelector('[data-active-storage-compact-target="pdfPages"]')) {
          await applyPdfPageCount(blob.signed_id, file, newField)
        }
      } catch (error) {
        if (signal.aborted) return
        failed = true
        this.removePendingField()
        if (this.hasStatusTextTarget) {
          this.statusTextTarget.textContent = `Failed to upload ${file.name}. ${error instanceof Error ? error.message : "Please try again."}`
        }
        console.error(error)
        break
      }
    }

    if (signal.aborted) return
    if (!failed && this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = this.cancelled ? "Remaining uploads cancelled." : "Upload complete."
    }
    this.uploading = false
    this.restoreSubmitButtons()
    if (this.hasInputTarget) this.inputTarget.disabled = false
    if (this.hasTriggerTarget) this.triggerTarget.classList.remove("hidden")
    if (this.hasProgressContainerTarget) this.progressContainerTarget.classList.add("hidden")
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.classList.add("hidden")
    if (!failed) this.resetTimer = setTimeout(() => this.resetUploadUi(), 2000)
  }

  showUploadUi() {
    this.uploading = true
    this.releaseUploadLock = lockFormDuringUpload(this.element)
    if (this.hasInputTarget) this.inputTarget.disabled = true
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.classList.remove("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.add("hidden")
    if (this.hasProgressContainerTarget) this.progressContainerTarget.classList.remove("hidden")
    if (this.hasControlsTarget) this.controlsTarget.classList.remove("hidden")
  }

  removePendingField() {
    if (!this.pendingField) return
    ;(this.pendingField.closest("turbo-frame") || this.pendingField).remove()
    this.pendingField = null
  }

  restoreSubmitButtons() {
    this.releaseUploadLock?.()
    this.releaseUploadLock = null
  }

  resetUploadUi() {
    if (this.hasTriggerTarget) this.triggerTarget.classList.remove("hidden")
    if (this.hasProgressContainerTarget) this.progressContainerTarget.classList.add("hidden")
    if (this.hasControlsTarget) this.controlsTarget.classList.add("hidden")
    if (this.hasInputTarget) this.inputTarget.value = ""
  }

  async appendNestedLineViaTurbo(signal) {
    if (!this.hasNewFileUrlValue) {
      throw new Error("active-storage-multiple requires newFileUrl")
    }

    const response = await fetch(this.newFileUrlValue, {
      method: "GET",
      credentials: "same-origin",
      signal,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })

    if (!response.ok) {
      throw new Error(`active-storage-multiple: nested #new failed (${response.status})`)
    }

    const html = await response.text()
    if (!html.includes("<turbo-stream")) {
      throw new Error("active-storage-multiple: nested #new returned a non-turbo-stream response")
    }

    const streamDocument = new DOMParser().parseFromString(html, "text/html")
    const frameId = streamDocument.querySelector("turbo-stream template")?.content.querySelector("turbo-frame")?.id

    signal.throwIfAborted()
    renderStreamMessage(html)

    const line = await waitForElement(() => {
      if (frameId) {
        return document.getElementById(frameId)?.querySelector('[data-nested-line-list-target="line"]')
      }

      const list = this.nestedLineList
      if (!list) return null

      const lines = list.querySelectorAll('[data-nested-line-list-target="line"]')
      return lines[lines.length - 1] ?? null
    }, { attempts: 30 })

    if (!line) {
      throw new Error("active-storage-multiple: appended row not found")
    }

    return line
  }

  get nestedLineList() {
    return this.hasListTarget ? this.listTarget : null
  }

  applyCategoryFromFilename(fieldElement, filename) {
    const categorySelect = fieldElement.querySelector('select[name*="[category]"]')
    if (!categorySelect) return

    const category = detectCategoryFromFilename(filename)
    if ([...categorySelect.options].some((option) => option.value === category)) {
      categorySelect.value = category
    }
  }
}
